import { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import {
  TextBolder,
  TextItalic,
  Code,
  Link,
  ListBullets,
  ListNumbers,
  Quotes,
  Minus,
  CaretDown,
  Table
} from 'phosphor-react';

interface RichTextEditorProps {
  /** HTML content */
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
}

const ALLOWED_TAGS = new Set([
  'p', 'br', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'code', 'a',
  'ul', 'ol', 'li', 'blockquote', 'hr',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'target', 'rel', 'class']),
  td: new Set(['class', 'colspan', 'rowspan']),
  th: new Set(['class', 'colspan', 'rowspan']),
  tr: new Set(['class']),
  table: new Set(['class']),
  thead: new Set(['class']),
  tbody: new Set(['class']),
  h1: new Set(['class']),
  h2: new Set(['class']),
  h3: new Set(['class']),
  h4: new Set(['class']),
  p: new Set(['class']),
  code: new Set(['class']),
  span: new Set(['class']),
  div: new Set(['class']),
};

function sanitizeHtml(html: string): string {
  if (typeof document === 'undefined') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const sanitize = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) return;

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tag)) {
      el.replaceWith(...Array.from(el.childNodes));
      return;
    }

    const allowed = ALLOWED_ATTRS[tag];
    if (allowed) {
      const toRemove: string[] = [];
      el.getAttributeNames().forEach(name => {
        if (!allowed.has(name.toLowerCase())) toRemove.push(name);
      });
      toRemove.forEach(name => el.removeAttribute(name));
    } else {
      el.getAttributeNames().forEach(name => el.removeAttribute(name));
    }

    if (tag === 'a') {
      const href = el.getAttribute('href') || '';
      if (!href.startsWith('http://') && !href.startsWith('https://') && !href.startsWith('mailto:')) {
        el.setAttribute('href', '#');
      }
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer');
    }

    Array.from(el.childNodes).forEach(sanitize);
  };

  Array.from(doc.body.childNodes).forEach(sanitize);
  return doc.body.innerHTML;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Digite seu conteúdo...',
  minHeight = '600px'
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [headerDropdownOpen, setHeaderDropdownOpen] = useState(false);
  const lastHtmlRef = useRef('');
  const savedRangeRef = useRef<Range | null>(null);

  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    } else {
      savedRangeRef.current = null;
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (!editorRef.current) return false;
    if (savedRangeRef.current) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current);
        return true;
      }
    }
    return false;
  }, []);

  const updateContent = useCallback(() => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    if (html !== lastHtmlRef.current) {
      lastHtmlRef.current = html;
      onChange(html);
    }
  }, [onChange]);

  const execCommand = useCallback((command: string, valueArg?: string) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    restoreSelection();
    if (command === 'bold' || command === 'italic') {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (!range.collapsed) {
          const tag = command === 'bold' ? 'strong' : 'em';
          const element = document.createElement(tag);
          try {
            range.surroundContents(element);
            range.collapse(false);
            updateContent();
            return;
          } catch {
            // fallback to execCommand
          }
        }
      }
    }
    const success = document.execCommand(command, false, valueArg);
    if (success) updateContent();
  }, [updateContent, restoreSelection]);

  const handleInput = useCallback(() => updateContent(), [updateContent]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    if (html) {
      const clean = sanitizeHtml(html);
      document.execCommand('insertHTML', false, clean || text);
    } else {
      document.execCommand('insertText', false, text);
    }
    updateContent();
  }, [updateContent]);

  const applyHeader = useCallback((level: number) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    restoreSelection();
    document.execCommand('formatBlock', false, `h${level}`);
    updateContent();
    setHeaderDropdownOpen(false);
  }, [updateContent, restoreSelection]);

  const insertTable = useCallback(() => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    restoreSelection();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const table = document.createElement('table');
    table.className = 'w-full my-4 border-collapse border theme-border';
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    headerRow.className = 'bg-[color:color-mix(in_oklab,var(--card-bg)_90%,transparent)]';
    for (let i = 0; i < 3; i++) {
      const th = document.createElement('th');
      th.className = 'px-4 py-2 border theme-border theme-text font-semibold';
      th.textContent = `Coluna ${i + 1}`;
      headerRow.appendChild(th);
    }
    thead.appendChild(headerRow);
    table.appendChild(thead);
    const tbody = document.createElement('tbody');
    for (let i = 0; i < 2; i++) {
      const tr = document.createElement('tr');
      for (let j = 0; j < 3; j++) {
        const td = document.createElement('td');
        td.className = 'px-4 py-2 border theme-border theme-text';
        td.textContent = `Linha ${i + 1}`;
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    range.insertNode(table);
    range.collapse(false);
    updateContent();
  }, [updateContent, restoreSelection]);

  const insertHR = useCallback(() => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    restoreSelection();
    const hr = document.createElement('hr');
    hr.className = 'my-4 border-t theme-border';
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(hr);
      range.collapse(false);
      updateContent();
    }
  }, [updateContent, restoreSelection]);

  const insertLink = useCallback(() => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    restoreSelection();
    const selection = window.getSelection();
    const text = selection?.toString() || 'link';
    const url = prompt('Digite a URL:', text);
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.textContent = text;
      link.className = 'text-[color:var(--nt-primary)] underline hover:opacity-80';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(link);
        range.collapse(false);
        updateContent();
      }
    }
  }, [updateContent, restoreSelection]);

  const ensureEmptyBlock = useCallback(() => {
    if (!editorRef.current) return;
    const el = editorRef.current;
    if (!el.innerHTML.trim() || el.innerHTML === '<br>') {
      el.innerHTML = '<p><br></p>';
      lastHtmlRef.current = el.innerHTML;
      onChange(el.innerHTML);
      const p = el.querySelector('p');
      if (p) {
        const range = document.createRange();
        range.setStart(p, 0);
        range.collapse(true);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  }, [onChange]);

  useLayoutEffect(() => {
    if (!editorRef.current) return;
    const initial = (value ?? '').trim() || '';
    const htmlToSet = initial || '<p><br></p>';
    if (editorRef.current.innerHTML !== htmlToSet) {
      editorRef.current.innerHTML = htmlToSet;
    }
    lastHtmlRef.current = editorRef.current.innerHTML;
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (headerDropdownOpen && !(e.target as Element).closest('.header-dropdown-container')) {
        setHeaderDropdownOpen(false);
      }
    };
    if (headerDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [headerDropdownOpen]);

  const keepFocus = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    saveSelection();
  }, [saveSelection]);

  return (
    <div className="w-full h-full bg-[color:var(--background)] relative flex flex-col">
      <div className="flex items-center gap-1 px-4 py-2 border-b theme-border flex-shrink-0">
        <div className="relative header-dropdown-container">
          <button
            type="button"
            onMouseDown={keepFocus}
            onClick={() => setHeaderDropdownOpen(!headerDropdownOpen)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors"
            title="Títulos"
          >
            <span className="text-xs font-semibold">T</span>
            <CaretDown className="w-3 h-3" weight="bold" />
          </button>
          {headerDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-48 rounded-lg border theme-border theme-bg shadow-lg overflow-hidden z-20">
              {[1, 2, 3, 4].map(level => (
                <button
                  key={level}
                  type="button"
                  onMouseDown={keepFocus}
                  onClick={() => applyHeader(level)}
                  className="w-full px-3 py-2 text-left text-sm theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors"
                >
                  <div className={level === 1 ? 'text-2xl' : level === 2 ? 'text-xl' : level === 3 ? 'text-lg' : 'text-base'} style={{ fontWeight: 700 }}>Título {level}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-[color:var(--card-border)] mx-1" />

        <button type="button" onMouseDown={keepFocus} onClick={() => { ensureEmptyBlock(); execCommand('bold'); }} className="p-1.5 rounded-lg theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors" title="Negrito (Ctrl+B)">
          <TextBolder className="w-4 h-4" weight="bold" />
        </button>
        <button type="button" onMouseDown={keepFocus} onClick={() => { ensureEmptyBlock(); execCommand('italic'); }} className="p-1.5 rounded-lg theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors" title="Itálico (Ctrl+I)">
          <TextItalic className="w-4 h-4" weight="bold" />
        </button>
        <button
          type="button"
          onMouseDown={keepFocus}
          onClick={() => {
            if (!editorRef.current) return;
            ensureEmptyBlock();
            editorRef.current.focus();
            restoreSelection();
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              const code = document.createElement('code');
              code.className = 'bg-[color:var(--chat-code-bg)] text-[color:var(--accent-pink-500)] px-1.5 py-0.5 rounded text-sm font-mono';
              code.textContent = selection.toString() || 'código';
              range.deleteContents();
              range.insertNode(code);
              range.collapse(false);
              updateContent();
            }
          }}
          className="p-1.5 rounded-lg theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors"
          title="Código"
        >
          <Code className="w-4 h-4" weight="bold" />
        </button>
        <button type="button" onMouseDown={keepFocus} onClick={() => { ensureEmptyBlock(); insertLink(); }} className="p-1.5 rounded-lg theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors" title="Link">
          <Link className="w-4 h-4" weight="bold" />
        </button>

        <div className="w-px h-6 bg-[color:var(--card-border)] mx-1" />

        <button type="button" onMouseDown={keepFocus} onClick={() => { ensureEmptyBlock(); execCommand('insertUnorderedList'); }} className="p-1.5 rounded-lg theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors" title="Lista com marcadores">
          <ListBullets className="w-4 h-4" weight="bold" />
        </button>
        <button type="button" onMouseDown={keepFocus} onClick={() => { ensureEmptyBlock(); execCommand('insertOrderedList'); }} className="p-1.5 rounded-lg theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors" title="Lista numerada">
          <ListNumbers className="w-4 h-4" weight="bold" />
        </button>
        <button type="button" onMouseDown={keepFocus} onClick={() => { ensureEmptyBlock(); execCommand('formatBlock', 'blockquote'); }} className="p-1.5 rounded-lg theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors" title="Citação">
          <Quotes className="w-4 h-4" weight="bold" />
        </button>
        <button type="button" onMouseDown={keepFocus} onClick={() => { ensureEmptyBlock(); insertHR(); }} className="p-1.5 rounded-lg theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors" title="Linha horizontal">
          <Minus className="w-4 h-4" weight="bold" />
        </button>
        <button type="button" onMouseDown={keepFocus} onClick={() => { ensureEmptyBlock(); insertTable(); }} className="p-1.5 rounded-lg theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors" title="Tabela">
          <Table className="w-4 h-4" weight="bold" />
        </button>
      </div>

      <div className="flex-1 relative overflow-hidden">
        <style>{`
          [contenteditable="true"] p { margin: 0 !important; padding: 0 !important; line-height: 1.5 !important; }
          [contenteditable="true"] p + p { margin-top: 0.5rem !important; }
          [contenteditable="true"] h1, [contenteditable="true"] h2, [contenteditable="true"] h3, [contenteditable="true"] h4 { margin-top: 1rem !important; margin-bottom: 0.5rem !important; }
          [contenteditable="true"] strong, [contenteditable="true"] b, [contenteditable="true"] em, [contenteditable="true"] i, [contenteditable="true"] code, [contenteditable="true"] a { display: inline !important; margin: 0 !important; padding: 0 !important; }
        `}</style>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePaste}
          onFocus={() => setIsFocused(true)}
          onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
            const related = e.relatedTarget as Node | null;
            if (editorRef.current && related && !editorRef.current.contains(related)) {
              saveSelection();
            }
            setIsFocused(false);
            updateContent();
          }}
          className="w-full h-full px-8 py-8 max-md:px-4 max-md:py-4 text-sm theme-text bg-transparent resize-none focus:outline-none overflow-auto"
          style={{
            minHeight: minHeight === '100%' ? '100%' : minHeight,
            height: minHeight === '100%' ? '100%' : undefined,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            caretColor: 'var(--nt-primary)',
            lineHeight: '1.5',
          }}
          onKeyDown={e => {
            if (e.key === 'Tab' && !e.metaKey && !e.ctrlKey) {
              e.preventDefault();
              document.execCommand('insertText', false, '\t');
              updateContent();
            }
          }}
          data-placeholder={placeholder}
        />
        {!value.trim() && !isFocused && (
          <div className="absolute pointer-events-none px-8 py-8 max-md:px-4 max-md:py-4 text-sm theme-text-muted italic" style={{ top: 0, left: 0, right: 0 }}>
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}
